import { isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';

import { TreatmentEpisodeProgramDetails } from '~/components/maud/patients/treatmentEpisode/Types';
import useListEditState from './useListEditState';
import { getNextProgramStartDate } from '../util';

const defaultTreatmentEpisodeProgramsState: TreatmentEpisodeProgramDetails = {
  endDate: undefined,
  startDate: undefined,
  treatmentEpisodeProgramId: undefined,
  treatmentProgramClient: {
    code: undefined,
    isDeleted: undefined,
    label: undefined,
  },
  treatmentProgramName: undefined,
};

export const useEditTreatmentEpisodeProgramsState = (
  treatmentEpisode?: TreatmentEpisode,
): UpdateTreatmentEpisodeProgramsState => {
  const initialState: TreatmentEpisodeProgramDetails[] = treatmentEpisode
    ? (treatmentEpisode.treatmentPrograms || []).map((p) => ({
        endDate: p?.endDate,
        externalId: p.treatmentEpisodeProgramId,
        startDate: p?.startDate,
        treatmentProgramName: p.programName
          ? p.programName
          : p.treatmentProgramName,
      }))
    : [];
  const { isChanged, isMissingFields, values, add, remove, reset, update } =
    useListEditState<TreatmentEpisodeProgramDetails>(
      (data) => ({
        ...defaultTreatmentEpisodeProgramsState,
        ...data,
        key: uuidv4(),
      }),
      (item) => isNil(item?.startDate) || isNil(item?.treatmentProgramName),
      initialState,
    );

  const lastProgramEndDate = useMemo<string>(
    () => values[values.length - 1]?.endDate,
    [values],
  );

  const addProgram = (admitDate: Date) => {
    const programStartDate = lastProgramEndDate
      ? getNextProgramStartDate(lastProgramEndDate)
      : DateTime.fromJSDate(admitDate).toISODate();

    add({
      ...defaultTreatmentEpisodeProgramsState,
      startDate: programStartDate,
    });
  };

  const updateProgram = (
    index: number,
    data: Partial<TreatmentEpisodeProgramDetails>,
  ) => {
    update(index, data);
    const updateKey = Object.keys(data)[0];
    if (updateKey === 'endDate') {
      const updateValue = Object.values(data)[0] as string;
      const nextProgramIndex = index + 1;
      const nextProgramStartDate = getNextProgramStartDate(updateValue);
      update(nextProgramIndex, {
        startDate: nextProgramStartDate,
      });
    }
  };

  const resetState = (
    state?: TreatmentEpisodeProgramDetails[],
    isChanged: boolean = false,
  ) => {
    reset(
      (state || initialState).map((p) => ({
        endDate: p?.endDate,
        externalId: p.treatmentEpisodeProgramId,
        startDate: p?.startDate,
        treatmentProgramName: p.programName
          ? p.programName
          : p.treatmentProgramName,
      })),
      isChanged,
    );
  };

  useEffect(() => {
    resetState(treatmentEpisode?.treatmentPrograms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentEpisode]);

  return {
    addProgram,
    isChanged,
    isMissingFields,
    lastProgramEndDate,
    removeProgram: remove,
    resetState,
    updateProgram,
    values,
  };
};
